import React, { useEffect, useState } from 'react';
import DockVerticalDerecha from '../estructura/DockVerticalDerecha';
import { toast } from 'react-toastify';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import PlantillaModal from '../util/PlantillaModal';
import { useContextoSesion } from '../../miscelaneo/ContextoSesion';
import FuncionesProcesoGastoMenor from './Funciones/FuncionesProcesoGastoMenor';
import FuncionesGenerales from '../util/FuncionesGenerales';
import SpinnerCargando from '../util/SpinnerCargando';
import TablaEgreso from '../util/tablas/TablaEgreso';
import ModalPDFBase64 from '../util/ModalPDFBase64';
import ModalConfirmacion from '../util/ModalConfirmacion';
import { FaPlus, FaTrash } from 'react-icons/fa';

/**
 * Componente para gestionar Gastos Menores (E43)
 * Permite crear, ver y editar egresos con envío automático a DGII
 */
export default function ProcesoGastoMenor() {
  const { usuario } = useContextoSesion();
  const { obtenerFechaHoraActual, formatearFechaHoraInput, formatoMoneda } = FuncionesGenerales(usuario);
  const { generarCamposFormulario, calcularTotalesEgreso } = FuncionesProcesoGastoMenor();

  // Estados principales
  const [formData, setFormData] = useState({
    detallesEgreso: [],
    es_gasto_menor: true,
    generarComprobante: false
  });
  const [modo, setModo] = useState('creacion');
  const [editable, setEditable] = useState(true);
  const [cargando, setCargando] = useState(false);
  const [campos, setCampos] = useState([]);

  // Estados para datos del formulario
  const [tiposEgreso, setTiposEgreso] = useState([]);
  const [cuentasTesoreria, setCuentasTesoreria] = useState([]);
  const [metodosPago, setMetodosPago] = useState([]);

  // Estados para modales
  const [mostrarModalEgresos, setMostrarModalEgresos] = useState(false);
  const [mostrarPDFEgreso, setMostrarPDFEgreso] = useState(false);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [cargandoPDF, setCargandoPDF] = useState(false);
  
  // Estados para confirmación de serie B
  const [mostrarConfirmacionSerieB, setMostrarConfirmacionSerieB] = useState(false);
  const [payloadPendiente, setPayloadPendiente] = useState(null);

  // Estado para checkbox de comprobante fiscal (por defecto sin NCF)
  const [generarComprobante, setGenerarComprobante] = useState(false);

  // Agrupar campos por sección
  const camposPorSeccion = campos.reduce((acc, campo) => {
    if (!acc[campo.seccion]) acc[campo.seccion] = [];
    acc[campo.seccion].push(campo);
    return acc;
  }, {});

  // Efecto: cambio de modo
  useEffect(() => {
    setEditable(modo === 'creacion');

    if (modo === 'ver') {
      toast.info('Modo de observación');
    }
    if (modo === 'creacion') {
      toast.info('Modo de creación');
    }
    if (modo === 'edicion') {
      toast.info('Modo de edición');
    }
  }, [modo]);

  // Efecto: cargar datos iniciales
  useEffect(() => {
    const fetchData = async () => {
      try {
        setCargando(true);

        const [resTiposEgreso, resCuentasTesoreria, resMetodosPago] = await Promise.all([
          instanciaAxios.get('/miscelaneo/tipo_egreso'),
          instanciaAxios.get('/cuenta_tesoreria'),
          instanciaAxios.get('/miscelaneo/metodo_pago')
        ]);

        setTiposEgreso(resTiposEgreso.data || []);
        setCuentasTesoreria(resCuentasTesoreria.data || []);
        setMetodosPago(resMetodosPago.data || []);

        setCampos(generarCamposFormulario(
          resTiposEgreso.data || [],
          resCuentasTesoreria.data || [],
          resMetodosPago.data || []
        ));

        setFormData(prev => ({
          ...prev,
          fecha_hora: obtenerFechaHoraActual(),
          usuario: usuario.nombre + ' ' + (usuario.apellido || '')
        }));

      } catch (error) {
        if (esErrorPermiso(error)) return;
        console.error('Error fetching data:', error);
        toast.error('Error al cargar datos iniciales');
      } finally {
        setCargando(false);
      }
    };

    fetchData();
  }, []);

  // Efecto: recalcular totales cuando cambian los detalles
  useEffect(() => {
    const totales = calcularTotalesEgreso(formData.detallesEgreso);
    setFormData(prev => ({
      ...prev,
      monto: totales.monto,
      monto_total: totales.monto_total
    }));
  }, [formData.detallesEgreso]);

  // Handlers
  const handleChange = (e) => {
    const { id, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: type === 'checkbox' ? checked : value
    }));
  };

  function limpiarCampos() {
    setFormData({
      detallesEgreso: [],
      es_gasto_menor: true,
      generarComprobante: false,
      tiene_e43: false,
      id_egreso: null,
      ncf: null,
      dgii_track_id: null,
      fecha_hora: obtenerFechaHoraActual(),
      usuario: usuario.nombre + ' ' + (usuario.apellido || '')
    });
    setPdfBase64(null);
    setGenerarComprobante(false); // Reset checkbox (por defecto sin NCF)
  }

  // =========================================================================
  // HANDLERS PARA DETALLES INLINE (sin TablaDinamica)
  // =========================================================================

  const agregarLineaVacia = () => {
    if (!editable || formData.tiene_e43) return;
    
    // Verificar que todas las líneas existentes estén completas
    const lineasIncompletas = formData.detallesEgreso.some(
      det => !det.descripcion || !det.monto || det.monto <= 0
    );
    
    if (lineasIncompletas) {
      toast.warning('Complete las líneas existentes antes de agregar una nueva');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      detallesEgreso: [
        ...prev.detallesEgreso,
        { descripcion: '', cantidad: 1, monto: 0, total: 0 }
      ]
    }));
  };

  const actualizarLinea = (index, campo, valor) => {
    if (!editable || formData.tiene_e43) return;

    setFormData(prev => {
      const nuevosDetalles = [...prev.detallesEgreso];
      nuevosDetalles[index] = {
        ...nuevosDetalles[index],
        [campo]: valor
      };
      
      // Recalcular total de la línea
      const cantidad = parseFloat(nuevosDetalles[index].cantidad) || 1;
      const monto = parseFloat(nuevosDetalles[index].monto) || 0;
      nuevosDetalles[index].total = cantidad * monto;

      return { ...prev, detallesEgreso: nuevosDetalles };
    });
  };

  const eliminarLinea = (index) => {
    if (!editable || formData.tiene_e43) return;

    setFormData(prev => ({
      ...prev,
      detallesEgreso: prev.detallesEgreso.filter((_, i) => i !== index)
    }));
  };

  // =========================================================================
  // HANDLER PARA REENVIAR A DGII
  // =========================================================================

  const handleReenviarADGII = async () => {
    if (!formData.id_egreso) {
      toast.error('No hay egreso seleccionado');
      return;
    }

    if (formData.tiene_e43) {
      toast.info('Este egreso ya fue enviado a DGII');
      return;
    }

    try {
      setCargando(true);
      
      const respuesta = await instanciaAxios.post(`/egreso/${formData.id_egreso}/enviar-dgii`);
      
      if (respuesta.data?.data?.pdf?.pdf_base64) {
        setPdfBase64(respuesta.data.data.pdf.pdf_base64);
        setMostrarPDFEgreso(true);
        
        setFormData(prev => ({
          ...prev,
          tiene_e43: true,
          dgii_track_id: respuesta.data.data.dgii?.trackId
        }));
        
        toast.success('Egreso enviado a DGII exitosamente');
      } else if (respuesta.data?.data?.dgii?.trackId) {
        setFormData(prev => ({
          ...prev,
          tiene_e43: true,
          dgii_track_id: respuesta.data.data.dgii.trackId
        }));
        toast.success('Egreso enviado a DGII exitosamente');
      } else {
        toast.warning('Egreso reenviado pero sin confirmación de PDF');
      }
    } catch (error) {
      console.error('Error al reenviar a DGII:', error);
      toast.error(error.response?.data?.mensaje || 'Error al reenviar a DGII');
    } finally {
      setCargando(false);
    }
  };

  // =========================================================================
  // HANDLER PARA VER PDF
  // =========================================================================

  const handleMostrarPDFEgreso = async () => {
    if (!formData.id_egreso) {
      toast.error('Debe seleccionar un egreso primero');
      return;
    }

    try {
      setCargandoPDF(true);
      
      const respuesta = await instanciaAxios.get(`/egreso/${formData.id_egreso}/pdf`);
      
      if (respuesta.data?.data?.pdf_base64) {
        setPdfBase64(respuesta.data.data.pdf_base64);
        setMostrarPDFEgreso(true);
      } else {
        toast.error('No se pudo obtener el PDF');
      }
    } catch (error) {
      console.error('Error al obtener PDF:', error);
      toast.error('Error al obtener el PDF del egreso');
    } finally {
      setCargandoPDF(false);
    }
  };

  // =========================================================================
  // SUBMIT Y NAVEGACIÓN
  // =========================================================================

  async function submitEgreso() {
    if (cargando) return;
    if (modo === 'ver') {
      toast.error('No se puede guardar cambios en modo de observación');
      return;
    }

    try {
      // Limpiar líneas vacías antes de validar
      const detallesLimpios = (formData.detallesEgreso || []).filter(
        det => det.descripcion && det.monto && det.monto > 0
      );

      // Actualizar formData con detalles limpios y flag de comprobante
      const formDataLimpio = {
        ...formData,
        detallesEgreso: detallesLimpios,
        generarComprobante // Indica si generar NCF o no
      };

      // Validaciones
      const errores = [
        { cond: !formDataLimpio.concepto, msg: 'El concepto es obligatorio' },
        { cond: !formDataLimpio.id_tipo_egreso, msg: 'Debe seleccionar un tipo de egreso' },
        { cond: !formDataLimpio.id_cuenta_tesoreria, msg: 'Debe seleccionar una cuenta de tesorería' },
        { cond: detallesLimpios.length < 1, msg: 'Debe agregar al menos un detalle de gasto completo' }
      ];

      for (let error of errores) {
        if (error.cond) {
          toast.error(error.msg);
          return;
        }
      }

      setCargando(true);

      // Actualizar estado con detalles limpios
      setFormData(formDataLimpio);

      if (modo === 'creacion') {
        const respuesta = await instanciaAxios.post('/egreso', formDataLimpio);

        if (respuesta.data?.data?.pdf?.pdf_base64) {
          setPdfBase64(respuesta.data.data.pdf.pdf_base64);
          setMostrarPDFEgreso(true);

          setFormData(prev => ({
            ...prev,
            ncf: respuesta.data.data.ncf,
            id_egreso: respuesta.data.data.id_egreso,
            tiene_e43: respuesta.data.data.tiene_comprobante ?? respuesta.data.data.ncf !== null
          }));

          // Mensaje según tipo de egreso
          if (!respuesta.data.data.ncf) {
            // Sin comprobante fiscal
            toast.success('Egreso registrado exitosamente (sin comprobante fiscal)');
          } else if (respuesta.data.mensaje && respuesta.data.mensaje.includes('Serie B')) {
            // Detectar fallback a serie B
            toast.warning(
              `${respuesta.data.mensaje}\n\nNCF físico generado: ${respuesta.data.data.ncf}`,
              { autoClose: 10000 }
            );
          } else if (respuesta.data.data.es_electronica) {
            toast.success('Gasto menor E43 enviado a DGII exitosamente');
          } else {
            toast.success('Gasto menor con NCF físico registrado exitosamente');
          }

          setModo('ver');
          return;
        }

        if (respuesta.data?.data?.dgii_error) {
          toast.warning(`Egreso guardado pero no se pudo enviar a DGII: ${respuesta.data.data.dgii_error}`);
          setFormData(prev => ({
            ...prev,
            ncf: respuesta.data.data.ncf,
            id_egreso: respuesta.data.data.id_egreso
          }));
          setModo('ver');
          return;
        }

        if (respuesta.data?.data?.id_egreso) {
          setFormData(prev => ({
            ...prev,
            id_egreso: respuesta.data.data.id_egreso
          }));
        }

        toast.success(formDataLimpio.generarComprobante
          ? 'Gasto menor guardado correctamente'
          : 'Gasto menor guardado sin comprobante fiscal');
        setModo('ver');

      } else if (modo === 'edicion') {
        if (!formData.id_egreso) {
          toast.error('Debe seleccionar un egreso para actualizar');
          return;
        }

        await instanciaAxios.put(`/egreso/${formData.id_egreso}`, {
          comentario: formData.comentario
        });

        toast.success('Egreso actualizado correctamente');
        setModo('ver');
      }

    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al registrar egreso:', error);

      // Caso especial: Error de secuencias NCF (status 409)
      if (error.response?.status === 409) {
        const codigo = error.response?.data?.codigo;
        const puedeUsarB = error.response?.data?.puedeUsarB;
        
        // No hay serie E pero hay serie B disponible → mostrar confirmación
        if (codigo === 'SIN_SECUENCIA_E' && puedeUsarB) {
          // Limpiar líneas vacías para el payload pendiente
          const detallesLimpios = (formData.detallesEgreso || []).filter(
            det => det.descripcion && det.monto && det.monto > 0
          );
          
          setPayloadPendiente({
            ...formData,
            detallesEgreso: detallesLimpios
          });
          setMostrarConfirmacionSerieB(true);
          return;
        }
        
        // No hay secuencias disponibles (ni E ni B) → mensaje claro
        if (codigo === 'SIN_SECUENCIA_E' || codigo === 'SIN_SECUENCIA_B') {
          toast.error(
            '⚠️ No hay secuencias NCF disponibles para gastos menores. Configure una nueva secuencia en Configuración > Secuencias NCF.',
            { autoClose: 8000 }
          );
          return;
        }
      }

      if (error.response?.data?.mensaje) {
        toast.error(error.response.data.mensaje);
      } else {
        toast.error('Error al registrar gasto menor');
      }
    } finally {
      setCargando(false);
    }
  }

  // Confirmar creación con serie B
  async function confirmarCreacionSerieB() {
    if (cargando) return;
    if (!payloadPendiente) return;

    setMostrarConfirmacionSerieB(false);
    setCargando(true);
    try {
      const payloadSerieB = {
        ...payloadPendiente,
        usarSerieB: true
      };
      
      const respuestaB = await instanciaAxios.post('/egreso', payloadSerieB);

      if (respuestaB.data?.data?.pdf?.pdf_base64) {
        setPdfBase64(respuestaB.data.data.pdf.pdf_base64);
        setMostrarPDFEgreso(true);

        setFormData(prev => ({
          ...prev,
          ncf: respuestaB.data.data.ncf,
          id_egreso: respuestaB.data.data.id_egreso,
          tiene_e43: true
        }));
      }

      toast.success(`Gasto menor creado con comprobante físico (serie B): ${respuestaB.data?.data?.ncf || 'N/A'}`);
      setModo('ver');
    } catch (errorSerieB) {
      console.error('Error al crear egreso con serie B:', errorSerieB);
      toast.error(errorSerieB.response?.data?.mensaje || 'Error al crear gasto menor con serie B');
    } finally {
      setCargando(false);
      setPayloadPendiente(null);
    }
  }

  function handleBotonBuscar() {
    setMostrarModalEgresos(true);
    setModo('ver');
  }

  async function handleSeleccionarEgreso(id_egreso) {
    try {
      setMostrarModalEgresos(false);
      setCargando(true);

      const respuesta = await instanciaAxios.get(`/egreso/${id_egreso}`);

      if (respuesta.status === 200) {
        const datos = respuesta.data;

        const tieneNCF = !!(datos.NCF || datos.ncf);
        setFormData({
          id_egreso: datos.id_egreso,
          ncf: datos.NCF || datos.ncf,
          concepto: datos.concepto,
          fecha_hora: formatearFechaHoraInput(datos.fecha_hora),
          comentario: datos.comentario || '',
          monto: datos.monto,
          monto_total: datos.monto_total,
          es_gasto_menor: datos.es_gasto_menor,
          generarComprobante: !!(datos.NCF || datos.ncf),
          tiene_e43: datos.tiene_e43,
          dgii_track_id: datos.dgii_track_id,
          id_tipo_egreso: datos.id_tipo_egreso,
          id_cuenta_tesoreria: datos.id_cuenta_tesoreria,
          id_metodo_pago: datos.id_metodo_pago,
          usuario: datos.nombre_usuario || '',
          detallesEgreso: (datos.detalles_egreso || []).map(det => ({
            descripcion: det.descripcion || '',
            cantidad: parseFloat(det.cantidad) || 1,
            monto: parseFloat(det.monto) || 0,
            total: parseFloat(det.total) || 0,
            indicador_facturacion: det.indicador_facturacion || 4,
            indicador_bien_servicio: det.indicador_bien_servicio || 2,
            id_cuenta_contable: det.id_cuenta_contable
          }))
        });
        // Establecer checkbox según si tiene NCF o no
        setGenerarComprobante(tieneNCF);
      } else {
        toast.error('Error al seleccionar egreso');
      }
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar egreso');
    } finally {
      setCargando(false);
    }
  }

  function handleBotonEditar() {
    setModo('edicion');
    setMostrarModalEgresos(true);
  }

  function handleBotonCrear() {
    setModo('creacion');
    limpiarCampos();
  }

  if (cargando || cargandoPDF) {
    return <SpinnerCargando />;
  }

  return (
    <div className="min-h-screen bg-gray-200 font-sans">
      <div className="flex justify-center py-10">
        <div className="bg-gray-100 bg-opacity-90 rounded-xl border p-6 sm:p-12 w-full max-w-7xl mx-4 sm:mx-10">

          <form className="flex flex-col gap-8">
            {/* Header */}
            <div className="space-y-1">
              <h1 className="font-cascadia font-bold text-2xl">Gastos Menores</h1>
              <p className="text-gray-500">
                Registre gastos menores del personal (consumibles, transporte, parqueo, peajes, etc.)
              </p>
            </div>

            {/* Indicador de estado DGII */}
            {formData.tiene_e43 ? (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded-xl flex items-center gap-2">
                <span className="font-medium">✓ Enviado a DGII</span>
                {formData.dgii_track_id && (
                  <span className="text-sm">- Track ID: {formData.dgii_track_id}</span>
                )}
              </div>
            ) : formData.id_egreso && formData.ncf ? (
              <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-2 rounded-xl flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium">⚠ Pendiente de envío a DGII</span>
                  <span className="text-sm">- NCF: {formData.ncf}</span>
                </div>
                <button
                  type="button"
                  onClick={handleReenviarADGII}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-1 rounded-lg text-sm font-medium transition-colors"
                >
                  Reenviar a DGII
                </button>
              </div>
            ) : null}

            {/* Checkbox: Generar comprobante fiscal */}
            {modo === 'creacion' && (
              <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4">
                <input
                  type="checkbox"
                  id="generarComprobante"
                  checked={formData.generarComprobante || false}
                  onChange={handleChange}
                  disabled={!editable || formData.tiene_e43}
                  className="w-5 h-5 accent-color-primario cursor-pointer"
                />
                <label htmlFor="generarComprobante" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                  Generar comprobante fiscal (NCF/e-CF) para este gasto menor
                </label>
              </div>
            )}

            {/* Campos del formulario por sección */}
            {Object.entries(camposPorSeccion).map(([titulo, camposSeccion]) => (
              <div key={titulo}>
                <h2 className="text-lg font-semibold mb-4">{titulo}</h2>
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  {camposSeccion.map((campo) => (
                    <div key={campo.id} className="flex flex-col">
                      {campo.type === "select" ? (
                        <>
                          <label htmlFor={campo.id} className="text-sm font-medium text-gray-600 mb-1">
                            {campo.label}
                          </label>
                          <select
                            id={campo.id}
                            value={formData[campo.id] || ""}
                            onChange={handleChange}
                            className="w-full p-2 rounded-xl text-gray-700 border border-gray-400"
                            disabled={!editable || formData.tiene_e43}
                          >
                            <option value="">Seleccione una opción</option>
                            {campo.options?.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </>
                      ) : campo.type === "textarea" ? (
                        <>
                          <label htmlFor={campo.id} className="text-sm font-medium text-gray-600 mb-1">
                            {campo.label || 'Comentario'}
                          </label>
                          <textarea
                            id={campo.id}
                            className="w-full p-2 rounded-xl text-gray-700 border border-gray-400"
                            value={formData[campo.id] || ""}
                            onChange={handleChange}
                            placeholder={campo.placeholder}
                            rows={3}
                            disabled={modo === 'ver'}
                          />
                        </>
                      ) : (
                        <>
                          <label htmlFor={campo.id} className="text-sm font-medium text-gray-600 mb-1">
                            {campo.label}
                          </label>
                          <input
                            type={campo.type}
                            id={campo.id}
                            className="w-full p-2 rounded-xl text-gray-700 border border-gray-400"
                            value={formData[campo.id] || ""}
                            onChange={handleChange}
                            placeholder={campo.placeholder}
                            disabled={campo.disabled || !editable || formData.tiene_e43}
                          />
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </form>

          {/* ================================================================= */}
          {/* CHECKBOX COMPROBANTE FISCAL */}
          {/* ================================================================= */}
          <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="generarComprobante"
                checked={generarComprobante}
                onChange={(e) => setGenerarComprobante(e.target.checked)}
                disabled={modo !== 'creacion'}
                className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500 cursor-pointer"
              />
              <div>
                <label
                  htmlFor="generarComprobante"
                  className="text-sm font-medium text-gray-700 cursor-pointer"
                >
                  Generar comprobante fiscal (NCF)
                </label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Marcar solo si el gasto requiere documentación fiscal (E43 electrónico o B13 físico)
                </p>
              </div>
            </div>
            {modo !== 'creacion' && formData.ncf && (
              <p className="mt-2 text-sm text-emerald-600 font-medium">
                NCF generado: {formData.ncf}
              </p>
            )}
            {modo !== 'creacion' && !formData.ncf && (
              <p className="mt-2 text-sm text-gray-500 italic">
                Este egreso no tiene comprobante fiscal
              </p>
            )}
          </div>

          {/* ================================================================= */}
          {/* SECCIÓN DE DETALLES - Tabla inline responsive */}
          {/* ================================================================= */}
          <div className="mt-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Detalles del Gasto</h2>
              {editable && !formData.tiene_e43 && (
                <button
                  type="button"
                  onClick={agregarLineaVacia}
                  className="bg-color-secundario text-white rounded-xl px-4 py-2 hover:bg-color-secundario/90 transition-colors flex items-center gap-2"
                >
                  <FaPlus /> Agregar línea
                </button>
              )}
            </div>

            {/* Tabla responsive */}
            <div className="w-full overflow-x-auto">
              <table className="min-w-full text-sm text-gray-700">
                <thead className="bg-color-secundario text-white">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold min-w-[200px]">Descripción</th>
                    <th className="text-center px-3 py-2 font-semibold w-24">Cantidad</th>
                    <th className="text-right px-3 py-2 font-semibold w-32">Monto Unit.</th>
                    <th className="text-right px-3 py-2 font-semibold w-32">Total</th>
                    {editable && !formData.tiene_e43 && (
                      <th className="w-12"></th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {formData.detallesEgreso.length === 0 ? (
                    <tr>
                      <td colSpan={editable && !formData.tiene_e43 ? 5 : 4} className="text-center py-8 text-gray-400">
                        No hay detalles. {editable && !formData.tiene_e43 && 'Haga clic en "Agregar línea" para comenzar.'}
                      </td>
                    </tr>
                  ) : (
                    formData.detallesEgreso.map((detalle, index) => (
                      <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                        {/* Descripción */}
                        <td className="px-2 py-2">
                          {editable && !formData.tiene_e43 ? (
                            <input
                              type="text"
                              value={detalle.descripcion || ''}
                              onChange={(e) => actualizarLinea(index, 'descripcion', e.target.value)}
                              placeholder="Ej: Parqueo reunión"
                              className="w-full p-2 rounded-lg border border-gray-300 focus:border-color-primario focus:outline-none"
                            />
                          ) : (
                            <span>{detalle.descripcion}</span>
                          )}
                        </td>
                        
                        {/* Cantidad */}
                        <td className="px-2 py-2">
                          {editable && !formData.tiene_e43 ? (
                            <input
                              type="number"
                              min="1"
                              value={detalle.cantidad || 1}
                              onChange={(e) => actualizarLinea(index, 'cantidad', parseFloat(e.target.value) || 1)}
                              className="w-full p-2 rounded-lg border border-gray-300 text-center focus:border-color-primario focus:outline-none"
                            />
                          ) : (
                            <span className="block text-center">{detalle.cantidad}</span>
                          )}
                        </td>
                        
                        {/* Monto Unitario */}
                        <td className="px-2 py-2">
                          {editable && !formData.tiene_e43 ? (
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={detalle.monto || ''}
                              onChange={(e) => actualizarLinea(index, 'monto', parseFloat(e.target.value) || 0)}
                              placeholder="0.00"
                              className="w-full p-2 rounded-lg border border-gray-300 text-right focus:border-color-primario focus:outline-none"
                            />
                          ) : (
                            <span className="block text-right">{formatoMoneda(detalle.monto)}</span>
                          )}
                        </td>
                        
                        {/* Total */}
                        <td className="px-3 py-2 text-right font-medium">
                          {formatoMoneda(detalle.total || 0)}
                        </td>
                        
                        {/* Botón eliminar */}
                        {editable && !formData.tiene_e43 && (
                          <td className="px-2 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => eliminarLinea(index)}
                              className="text-red-500 hover:text-red-700 p-2"
                              title="Eliminar línea"
                            >
                              <FaTrash />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Resumen de totales */}
            <div className="mt-4 flex justify-end">
              <div className="bg-gray-50 border rounded-xl p-4 min-w-[200px]">
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total:</span>
                  <span className="text-color-primario">
                    {formatoMoneda(formData.monto_total || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Modal para buscar egresos */}
      <PlantillaModal mostrar={mostrarModalEgresos} onClose={() => setMostrarModalEgresos(false)}>
        <TablaEgreso setEgresoSeleccionado={handleSeleccionarEgreso} />
      </PlantillaModal>

      {/* Modal PDF para E43 */}
      <ModalPDFBase64
        mostrar={mostrarPDFEgreso}
        onClose={() => {
          setMostrarPDFEgreso(false);
          setPdfBase64(null);
        }}
        pdfBase64Directo={pdfBase64}
        ncf={formData.ncf}
        tituloDocumento="Comprobante Gasto Menor E43"
        tipoDocumento="gasto_menor"
      />

      {/* Modal de confirmación para usar Serie B */}
      <ModalConfirmacion
        mostrar={mostrarConfirmacionSerieB}
        onConfirmar={confirmarCreacionSerieB}
        onCancelar={() => {
          setMostrarConfirmacionSerieB(false);
          setPayloadPendiente(null);
        }}
        titulo="⚠️ Secuencia Electrónica No Disponible"
        mensaje={`No hay secuencias electrónicas (serie E) disponibles para este tipo de comprobante.\n\nHay secuencias físicas (serie B) disponibles.\n\n¿Desea crear el gasto menor con comprobante físico (serie B)?`}
        textoConfirmar="Sí, usar Serie B"
        textoCancelar="Cancelar"
        tipo="warning"
      />

      {/* Dock de acciones */}
      <DockVerticalDerecha
        handleBotonGuardar={() => submitEgreso()}
        handleBotonBuscar={() => handleBotonBuscar()}
        handleBotonEditar={() => handleBotonEditar()}
        handleBotonCrear={() => handleBotonCrear()}
        handleBotonPDF={formData.id_egreso ? handleMostrarPDFEgreso : undefined}
        guardando={cargando}
      />
    </div>
  );
}
